import { InputTextarea } from 'primereact/inputtextarea';
import {
    forwardRef,
    Ref,
    type DetailedHTMLProps,
    type ReactNode,
    type TextareaHTMLAttributes,
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { GetInputClassName } from '../../_utils/get-input-class-name';
import { getPlaceholder } from '../../_utils/get-placeholder';
import {
    buildRhfRules,
    type FieldLengthRule,
    type FieldValidate,
    lengthRuleValue,
    mergeRefs,
    warnAboutRegisterSpread,
} from '../../_utils/rhf-ref';
import {
    InputWrapper,
    InputWrapperSharedProps,
} from '../InputWrapper/InputWrapper';

type HTMLProps = DetailedHTMLProps<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
>;

export type TextAreaProps = HTMLProps &
    InputWrapperSharedProps & {
        /**
         * The input field name
         */
        name?: string;
        /**
         * Input field placeholder
         */
        placeholder?: string;
        /**
         * Input field value (this input is controlled only for now)
         */
        value?: string;
        /**
         * Additional custom styles (overrides)
         */
        disabled?: boolean;
        /**
         * Custom react-hook-form validation function
         */
        validate?: FieldValidate;
        /**
         * RHF minLength rule, only used when this input is RHF-controlled via `name`
         */
        minLength?: FieldLengthRule;
        /**
         * RHF maxLength rule, only used when this input is RHF-controlled via `name`
         */
        maxLength?: FieldLengthRule;
    };

function TextAreaComponent(
    props: TextAreaProps,
    ref: Ref<HTMLTextAreaElement>,
): ReactNode {
    const {
        name,
        required,
        bulkMode,
        errorMessage,
        horizontal,
        className: classNameProps,
        onBlur,
        labelHidden: __,
        labelClassName: ___,
        wrapperClassName: ____,
        validate,
        minLength,
        maxLength,
        'data-testid': dataTestId,
        ...restProps
    } = props;

    const testId = dataTestId || name;
    const placeholder = getPlaceholder(props);
    const formContext = useFormContext();
    const isControlled = typeof restProps.value !== 'undefined';
    const isRHFControlled = !isControlled && Boolean(formContext && name);
    warnAboutRegisterSpread({
        componentName: 'TextArea',
        isRHFControlled,
        onChange: restProps.onChange,
        ref,
    });
    if (!isRHFControlled) {
        return (
            <InputWrapper {...props}>
                <InputTextarea
                    {...restProps}
                    minLength={lengthRuleValue(minLength)}
                    maxLength={lengthRuleValue(maxLength)}
                    data-testid={testId}
                    className={GetInputClassName({
                        basicClassName: classNameProps,
                        horizontal,
                        invalidState: Boolean(errorMessage),
                    })}
                    placeholder={placeholder}
                    ref={ref}
                />
            </InputWrapper>
        );
    }

    return (
        <Controller
            name={name!}
            control={formContext!.control}
            rules={buildRhfRules({
                required,
                bulkMode,
                validate,
                minLength,
                maxLength,
            })}
            render={({ field, fieldState }) => (
                <InputWrapper field={field} fieldState={fieldState} {...props}>
                    <InputTextarea
                        {...restProps}
                        {...field}
                        minLength={lengthRuleValue(minLength)}
                        maxLength={lengthRuleValue(maxLength)}
                        data-testid={testId}
                        className={GetInputClassName({
                            basicClassName: classNameProps,
                            horizontal,
                            invalidState: fieldState.invalid,
                        })}
                        placeholder={placeholder}
                        ref={mergeRefs(ref, field.ref)}
                        onBlur={(event) => {
                            field.onBlur();
                            onBlur?.(event);
                        }}
                    />
                </InputWrapper>
            )}
        />
    );
}

export const TextArea = forwardRef(TextAreaComponent);
